import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Trash2, ChevronLeft, ChevronRight, BarChart3, Users, MapPin, Building2, RefreshCw, Download } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PIN = '2026';

const AdminDashboard = () => {
  const [authed, setAuthed] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/inquiries?skip=${page * LIMIT}&limit=${LIMIT}`),
        axios.get(`${API}/api/admin/stats`),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setHasMore(leadsRes.data.length === LIMIT);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (authed) fetchData();
  }, [authed, fetchData]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (pin === ADMIN_PIN) {
      setAuthed(true);
      setPinError('');
    } else {
      setPinError('Incorrect PIN');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`${API}/api/inquiries/${id}`);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Company', 'Project Type', 'Size', 'Location', 'Message', 'Date'];
    const rows = filtered.map((l) => [
      l.name, l.email, l.phone, l.company || '', l.project_type, l.warehouse_size || '', l.location, l.message || '',
      new Date(l.timestamp).toLocaleDateString('en-IN'),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `arthaa-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = leads.filter((l) => {
    const matchesSearch = search === '' || [l.name, l.email, l.phone, l.location, l.company].some((f) => (f || '').toLowerCase().includes(search.toLowerCase()));
    const matchesType = typeFilter === 'all' || l.project_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const projectTypes = [...new Set(leads.map((l) => l.project_type))];

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Helmet>
          <title>Admin | Arthaa Build</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <form onSubmit={handleLogin} className="bg-white p-8 rounded-xl shadow-lg border border-slate-200 w-full max-w-sm" data-testid="admin-login-form">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-[#1E3A5F] rounded-xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-serif font-bold text-2xl text-slate-900">Admin Access</h1>
            <p className="text-sm text-slate-500 mt-1">Enter PIN to view leads</p>
          </div>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Enter PIN"
            className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-lg tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-[#2B5278] mb-3"
            data-testid="admin-pin-input"
            autoFocus
          />
          {pinError && <p className="text-red-500 text-sm text-center mb-3" data-testid="admin-pin-error">{pinError}</p>}
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] text-white font-semibold py-3 rounded-lg"
            data-testid="admin-login-btn"
          >
            Access Dashboard
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Admin Dashboard | Arthaa Build</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Top Bar */}
      <header className="bg-[#1E3A5F] text-white px-6 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6" />
          <h1 className="font-serif font-bold text-lg">Arthaa Build — Leads</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchData} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="refresh-btn">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={exportCSV} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={() => setAuthed(false)} className="text-sm text-white/70 hover:text-white ml-2" data-testid="logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-cards">
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#2B5278]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#2B5278]" />
                </div>
                <span className="text-sm text-slate-500">Total Leads</span>
              </div>
              <p className="text-3xl font-bold text-slate-900" data-testid="stat-total">{stats.total_leads}</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#2B5278]/10 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-[#2B5278]" />
                </div>
                <span className="text-sm text-slate-500">Project Types</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">{Object.keys(stats.by_project_type).length}</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#2B5278]/10 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#2B5278]" />
                </div>
                <span className="text-sm text-slate-500">Locations</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">{Object.keys(stats.by_location).length}</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm text-slate-500">Top Type</span>
              </div>
              <p className="text-lg font-bold text-slate-900 capitalize">
                {Object.entries(stats.by_project_type).sort((a, b) => b[1] - a[1])[0]?.[0] || '—'}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search leads by name, email, phone, location..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B5278]"
              data-testid="search-input"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2B5278]"
            data-testid="type-filter"
          >
            <option value="all">All Types</option>
            {projectTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="leads-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden md:table-cell">Project</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden lg:table-cell">Location</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden lg:table-cell">Date</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">No leads found</td></tr>
                ) : (
                  filtered.map((lead) => (
                    <tr key={lead.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{lead.name}</div>
                        {lead.company && <div className="text-xs text-slate-500">{lead.company}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-700">{lead.email}</div>
                        <div className="text-xs text-slate-500">{lead.phone}</div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="inline-block px-2.5 py-1 bg-[#2B5278]/10 text-[#1E3A5F] text-xs font-medium rounded-full capitalize">
                          {lead.project_type}
                        </span>
                        {lead.warehouse_size && <div className="text-xs text-slate-500 mt-1">{lead.warehouse_size} sq ft</div>}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-600">{lead.location}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs">
                        {new Date(lead.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDelete(lead.id)}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <span className="text-xs text-slate-500">
              Page {page + 1} {stats ? `of ${Math.max(1, Math.ceil(stats.total_leads / LIMIT))}` : ''}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1.5 border border-slate-300 rounded-md disabled:opacity-30 hover:bg-white transition-colors"
                data-testid="prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore}
                className="p-1.5 border border-slate-300 rounded-md disabled:opacity-30 hover:bg-white transition-colors"
                data-testid="next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
